import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, ref } from 'vue'
import ProgressBar from './progress_bar.vue'
import Toggle from './toggle.vue'

interface DiffCellData {
  line_number: number | null;
  prefix: string;
  content: string;
}

// Props
type PropTypes = {
  diff_contents?: Promise<string[]>
  left_header?: string
  right_header?: string
  diff_max_height?: string
  progress?: number | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'diff',
  props: {
    diff_contents: { default: () => Promise.resolve([]) },
    left_header: { default: "" },
    right_header: { default: "" },
    diff_max_height: { default: '100%' },
    progress: { default: null }
  },
  setup(__props: any, { expose }) {

const props = __props as { diff_contents: Promise<string[]>, left_header: string, right_header: string, diff_max_height: string, progress: number | null };



// Reactive state object
const state = reactive({
  show_whitespace: false,
  fullscreen: false,
  loading: true,
  num_lines_rendered: 1000 // num_lines_per_page
})

// Non-reactive arrays for performance (as in original)
let left: DiffCellData[] = []
let right: DiffCellData[] = []

// Constants
const num_lines_per_page = 1000

const line_num_highlighting = {
  '- ': 'negative-line-num',
  '+ ': 'positive-line-num',
  '  ': ''
}

const content_highlighting = {
  '- ': 'negative',
  '+ ': 'positive',
  '  ': ''
}

const special_char_replacements: {[key: string]: string} = {
  ' ': '\u2219',
  '\t': '\u21e5\t',
  '\n': '\u21b5\n',
  '\r': '\\r\r',
  '\b': '\\b',  // backspace
  '\f': '\\f',  // form-feed
  '\v': '\\v',  // vertical tab
  '\0': '\\0',  // null character
}

// Computed properties
const left_with_whitespace = computed(() => {
  return left.map(cell_data => {
    return {
      line_number: cell_data.line_number,
      prefix: cell_data.prefix,
      content: replace_whitespace(cell_data.content)
    };
  });
})

const right_with_whitespace = computed(() => {
  return right.map(cell_data => {
    return {
      line_number: cell_data.line_number,
      prefix: cell_data.prefix,
      content: replace_whitespace(cell_data.content)
    };
  });
})

const whitespace_regex = computed(() => {
  // Some browsers might not yet support \p (unicode property escapes)
  try {
    // Match whitespace sequences and the "Other" unicode property category
    // https://unicode.org/reports/tr18/#General_Category_Property
    return new RegExp('[ \t\n\r\b\f\v\0]|\\p{C}', 'gu');
  }
  catch (e) {
    // It's unclear how/maybe impossible to mock a constructor call, so we
    // don't have a unit test for this fallback behavior currently.
    // istanbul ignore next
    return new RegExp('[ \t\n\r\b\f\v\0]', 'g');
  }
})

const num_lines_to_show = computed(() => {
  return Math.min(state.num_lines_rendered, left.length);
})

const line_num_width = computed(() => {
  return `${num_lines_to_show.value.toString().length + 1}ch`;
})

// Methods
const pad_if_needed = (left: DiffCellData[], right: DiffCellData[]) => {
  if (left.length === right.length) {
    return;
  }
  let to_pad: DiffCellData[];
  let bigger: DiffCellData[];
  if (left.length > right.length) {
    bigger = left;
    to_pad = right;
  }
  else {
    bigger = right;
    to_pad = left;
  }
  while (to_pad.length < bigger.length) {
    to_pad.push({line_number: null, prefix: ' ', content: ''});
  }
}

const replace_whitespace = (str: string): string => {
  return str.replace(whitespace_regex.value, (matched) => {
    if (matched in special_char_replacements) {
      return special_char_replacements[matched];
    }

    // Replace "Other" unicode characters with their escape sequences
    let unpadded_char_code = matched.charCodeAt(0).toString(16);
    let num_leading_zeros = Math.max(0, 4 - unpadded_char_code.length);
    return `\\u${('0'.repeat(num_leading_zeros) + unpadded_char_code)}`;
  });
}

const render_more_lines = () => {
  state.num_lines_rendered = Math.min(
    left.length,
    state.num_lines_rendered + num_lines_per_page
  );
}

// Initialize component (equivalent to created lifecycle)
const initialize = async () => {
  let left_line_number = 1;
  let right_line_number = 1;

  left = [];
  right = [];

  for (let item of await props.diff_contents) {
    let prefix = item.substring(0, 2);
    let content = item.substring(2);
    if (prefix === "- ") {
      left.push({line_number: left_line_number, prefix: prefix, content: content});
      left_line_number += 1;
    }
    else if (prefix === "  ") {
      pad_if_needed(left, right);

      left.push({line_number: left_line_number, prefix: prefix, content: content});
      right.push({line_number: right_line_number, prefix: prefix, content: content});

      left_line_number += 1;
      right_line_number += 1;
    }
    else if (prefix === "+ ") {
      right.push({line_number: right_line_number, prefix: prefix, content: content});
      right_line_number += 1;
    }
    else {  // Treat invalid prefixes as "+ "
      right.push({line_number: right_line_number, prefix: "+ ", content: item});
      right_line_number += 1;
    }
  }
  pad_if_needed(left, right);

  state.loading = false;
}

// Call initialization
initialize()

// Expose state for external access (tests, parent components)
expose({
  state
})

return { __sfc: true,props, state, left, right, num_lines_per_page, line_num_highlighting, content_highlighting, special_char_replacements, left_with_whitespace, right_with_whitespace, whitespace_regex, num_lines_to_show, line_num_width, pad_if_needed, replace_whitespace, render_more_lines, initialize, ProgressBar }
}

})